import { Ref, forwardRef } from 'react'

import { Box, Divider } from '@factorialco/deprecated-design-system'
import { F0TagRaw, IconType, TwoColumnLayout } from '@factorialco/f0-react'
import { F0TableOfContent } from '@factorialco/f0-react/dist/experimental'
import { motion } from 'framer-motion'

import i18n from 'modules/core/lib/i18n'

import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentConsumerCallbacks } from 'modules/trainings/components/content/types'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'
import { ConsumerCompletedState } from 'modules/trainings/pages/content/ConsumerCompletedState'

type TocItem = {
  id: string
  icon?: IconType
  label: string
  onClick: () => void
  disabled?: boolean
  children?: TocItem[]
}

const contentAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.25, ease: 'easeOut' },
}

const ContentConsumer = forwardRef(function ContentConsumer(
  {
    tocItems,
    selectedItem,
    updateNextButton,
    showEmptyState,
  }: {
    tocItems: TocItem[]
    selectedItem?: ContentItem
    updateNextButton: () => void
    showEmptyState: boolean
  },
  ref: Ref<ContentConsumerCallbacks>
) {
  return (
    <>
      <Divider />
      <TwoColumnLayout
        mainColumnPosition="right"
        sideContent={
          <F0TableOfContent
            activeItem={selectedItem?.uuid}
            items={tocItems}
            title={i18n.t('trainings.content.builder.title')}
          />
        }
      >
        {showEmptyState || !selectedItem ? (
          <Box alignItems="center" justifyContent="center" height="full">
            <ConsumerCompletedState />
          </Box>
        ) : (
          <motion.div
            key={selectedItem.uuid}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={contentAnimation}
            transition={contentAnimation.transition}
          >
            <Box alignItems="center">
              <Box flexDirection="row" alignSelf="start" height="s64">
                <div>
                  <ItemTag item={selectedItem} />
                </div>
              </Box>
              <RenderItem item={selectedItem} updateNextButton={updateNextButton} ref={ref} />
            </Box>
          </motion.div>
        )}
      </TwoColumnLayout>
    </>
  )
})

function ItemTag({ item }: { item: ContentItem }) {
  const icon = contentRegistry.fetch(item, 'icon')
  const label = contentRegistry.fetch(item, 'label')

  if (!icon || !label) return null

  return <F0TagRaw icon={icon} text={label()} />
}

const RenderItem = forwardRef(function RenderItem(
  { item, updateNextButton }: { item: ContentItem; updateNextButton: () => void },
  ref: Ref<ContentConsumerCallbacks>
) {
  const ConsumerComponent = contentRegistry.fetch(item, 'consumerComponent')
  if (!ConsumerComponent) return null

  return <ConsumerComponent contentItem={item} updateNextButton={updateNextButton} ref={ref} />
})

export default ContentConsumer
